<template>
  <AppContainer :is-loading>
    <div>
      <EPageTitleMobile :title="t(useTranslationPath(callerPath, 'title'))" />

      <EForm>
        <template v-if="existingUser">
          <EFormField
            id="user"
            :label="t(useTranslationPath(callerPath, 'existingUserLabel'))"
            :hint="t(useTranslationPath(callerPath, 'existingUserHint'))"
            :errors="getFieldErrors(errors, 'user')"
          >
            <EAutoComplete
              v-model="v$.user.$model"
              :on-search="searchUsers"
              value-key="id"
              label-key="name"
              :min-query-length="3"
            />
          </EFormField>

          <EButton
            :label="t(useTranslationPath(callerPath, 'createNewUserLabel'))"
            variant="link"
            @click="onExistingUserToggle"
          />
        </template>

        <template v-else>
          <EFormField
            id="newUser"
            :label="t(useTranslationPath(callerPath, 'newUserLabel'))"
            :errors="getFieldErrors(errors, 'name')"
          >
            <EInput id="newUser" v-model="v$.newUser.$model" name="newUser" />
          </EFormField>

          <EButton
            :label="t(useTranslationPath(callerPath, 'cancel'))"
            variant="link"
            @click="onExistingUserToggle"
          />
        </template>
      </EForm>
    </div>

    <template #bottom>
      <EButton
        :label="t(useTranslationPath(callerPath, 'next'))"
        block
        :disabled="v$.$invalid || isSubmitting"
        @click="onSubmit"
      />
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler.js'
import { useGoBack } from '@/composables/useGoBack'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore'
import { useUnauthenticatedTokenStore } from '@/stores/unauthenticated/UnauthenticatedTokenStore.js'
import {
  EAutoComplete,
  EButton,
  EForm,
  EFormField,
  EInput,
  EPageTitleMobile,
} from '@expansivefm/eds'
import useVuelidate from '@vuelidate/core'
import { minLength, requiredIf } from '@vuelidate/validators'
import { inject, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const { setCustomGoBack, resetGoBack } = useGoBack()
const $apiUnauthenticatedRequest = inject('$apiUnauthenticatedRequest')
const router = useRouter()
const { handleErrors, getFieldErrors } = useApiErrorHandler(router)

const unauthenticatedTokenStore = useUnauthenticatedTokenStore()
const appStore = useAppStore()

const callerPath = 'views.unauthenticated.user'

const isLoading = ref(null)
const existingUser = ref(true)
const errors = ref([])
const isSubmitting = ref(false)

const form = reactive({
  user: null,
  newUser: null,
})

const validationRules = {
  user: {
    requiredIf: requiredIf(() => existingUser.value),
  },
  newUser: {
    requiredIf: requiredIf(() => !existingUser.value),
    minLength: minLength(3),
  },
}

const v$ = useVuelidate(validationRules, form)

onMounted(async () => {
  appStore.$patch({ showBackButton: false })
  isLoading.value = true

  if (!unauthenticatedTokenStore.engineerCheckInAccessToken.contractor) {
    await router.push({
      name: 'auth.contractor',
    })
    return
  }

  setCustomGoBack(() =>
    router.push({
      name: 'auth.contractor',
    }),
  )

  isLoading.value = false
  appStore.$patch({ showBackButton: true })
})

onUnmounted(() => {
  resetGoBack()
})

const searchUsers = async (query) => {
  try {
    return await $apiUnauthenticatedRequest
      .index(
        `users`,
        $apiUnauthenticatedRequest.configs.index.setSort('name').setFilters({ name: query || '' }),
      )
      .then(({ data }) => {
        return data
      })
  } catch (error) {
    await handleErrors(error)
  }
}

const onExistingUserToggle = () => {
  existingUser.value = !existingUser.value
  form.user = null
  form.newUser = null
}

const onSubmit = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  isSubmitting.value = true

  const userId = existingUser.value ? form.user : await createUser()

  try {
    await $apiUnauthenticatedRequest.patch(
      'engineer-check-in-access-tokens',
      $apiUnauthenticatedRequest.configs.patch.setId(unauthenticatedTokenStore.tokenId).setData({
        user: userId,
      }),
    )

    await router.push({
      name: 'auth.verification.index',
    })
  } catch (error) {
    const result = await handleErrors(error)
    errors.value = result.validationErrors
  } finally {
    isSubmitting.value = false
  }
}

const createUser = async () => {
  try {
    const { data } = await $apiUnauthenticatedRequest.post(
      'users',
      $apiUnauthenticatedRequest.configs.post.setData({
        name: form.newUser,
      }),
    )

    return data.id
  } catch (error) {
    const result = await handleErrors(error)
    errors.value = result.validationErrors
    isLoading.value = false
  }
}
</script>
